.panel {
  display: grid;
  gap: var(--space-5);
}

/*
 * The one elevated surface on this panel, and deliberately not a masthead. The auction
 * panel's lead card carries a countdown because it has a deadline; this one carries a
 * finished figure, so it states a total and the sentence that bounds what the total is.
 */
.summary {
  display: grid;
  gap: var(--space-3);
}

.headline {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
}

.total {
  font-family: var(--font-figure);
  font-size: var(--text-2xl);
  font-weight: 600;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.totalLabel {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

/*
 * Canon § 1's qualification, in core's words. Treated as the sponsored disclosure is on the
 * auction panel — rule-marked furniture that is always present, never a warning that
 * appeared because something went wrong.
 */
.note {
  padding-block-start: var(--space-3);
  border-block-start: 1px solid var(--border);
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.scope {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.rows {
  display: grid;
  gap: var(--space-4);
  /* Not read as prose, so it clears its own marker: `base.css` zeroes list margin and
   * padding for every repo but deliberately leaves `list-style` alone (`APP-018`).
   * `role="list"` on the element keeps the semantics Safari drops with the marker. */
  list-style: none;
}

.row {
  display: grid;
  gap: var(--space-3);
  border-inline-start: 3px solid var(--brass-rule);
}

/*
 * A row that held no Top 10 position is edged in neutral rather than brass. The brass rule
 * is the board's rank furniture (contracts/DESIGN-TOKENS.md), and using it on an Honorable
 * Mention would be the design implying a standing that canon § 10 explicitly withholds.
 * Nothing here is carried by the edge alone: the row says "outside the Top 10" in words.
 */
.unranked {
  border-inline-start-color: var(--border-strong);
}

/*
 * Mobile-first: the mark, the names and the click figure stack. From 40rem the row becomes
 * a board line — position, product, result — with the figure at the end of the row.
 */
.head {
  display: grid;
  gap: var(--space-3);
}

@media (min-width: 40rem) {
  .head {
    grid-template-columns: 3.5rem minmax(0, 1fr) auto;
    align-items: baseline;
    gap: var(--space-4);
  }
}

/*
 * The rank mark, in the board's brass, matching the auction panel's marks so a founder reads
 * one vocabulary across the two panels. The position is always written out beside it as
 * well: canon forbids status by mark or colour alone.
 */
.mark {
  font-family: var(--font-figure);
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--brass);
  font-variant-numeric: tabular-nums;
}

/* An unranked row keeps the column and puts nothing in it. See HistoryPanel.tsx. */
.noMark {
  min-block-size: var(--space-1);
}

.names {
  display: grid;
  gap: var(--space-1);
  min-inline-size: 0;
}

.day {
  font-family: var(--font-figure);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.product {
  font-size: var(--text-lg);
  min-inline-size: 0;
}

.source {
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.label {
  color: var(--text);
  font-weight: 600;
}

/*
 * The result of the day. It sits at the end of the row on wide screens and above the
 * explanation on narrow ones, and its unit is written next to it rather than left to a
 * column header that a stacked layout would have thrown away.
 */
.clicks {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
}

@media (min-width: 40rem) {
  .clicks {
    justify-content: flex-end;
    text-align: end;
  }
}

.clickCount {
  font-family: var(--font-figure);
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.clickLabel {
  color: var(--text-muted);
  font-size: var(--text-sm);
}

/* Why the deal was on the board that day, and what it cost. */
.why {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

/*
 * The lifetime click figures (`APP-021`, canon decision `D-018`).
 *
 * Two figures side by side on a laptop and stacked on a phone, each with its meaning on the
 * line beneath it rather than in a shared heading. That layout is the requirement, not a
 * preference: `D-018` keeps Founding wall clicks out of canon § 11's lists, so a reader who
 * takes in one number and skips the label has to be wrong about what it counts for. Two
 * labelled columns make the split the first thing read; a single row of numbers under one
 * heading would make it the last.
 *
 * There is no total, no sum and no space where one could be added.
 */
.lifetime {
  display: grid;
  gap: var(--space-4);
}

.lifetimeTitle {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--text-base);
  font-weight: 600;
}

.lifetimeFigures {
  display: grid;
  gap: var(--space-4);
  margin: 0;
}

@media (min-width: 40rem) {
  .lifetimeFigures {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-6);
  }
}

.lifetimeFigure {
  display: grid;
  gap: var(--space-1);
  align-content: start;
}

.lifetimeNumber {
  font-family: var(--font-figure);
  font-size: var(--text-2xl);
  font-weight: 600;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.lifetimeLabel {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  max-inline-size: var(--measure);
}
